import { useState, useEffect, useCallback, useRef } from 'react';
import { calendarService } from '../api/services';
import socketService from '../api/socket';

const toStr = (v) => (v != null ? String(v) : '');

const byStart = (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime();

/**
 * Charge les événements calendrier et les maintient à jour en temps réel.
 * Écoute calendar:event:created / :updated / :deleted (room org:{id}).
 * Couvre le cas clé : un événement créé par l'agent IA apparaît sans recharger.
 *
 * @param {{startDate?: string, endDate?: string, type?: string, status?: string}} params
 */
export const useCalendarEvents = (params = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Sérialisé pour une dépendance stable du useCallback/useEffect.
  const paramsKey = JSON.stringify(params);

  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await calendarService.getEvents(JSON.parse(paramsKey));
      if (!mountedRef.current) return;
      // Enveloppe backend : { success, data: [...] }
      const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setEvents([...raw].sort(byStart));
    } catch (err) {
      if (mountedRef.current) {
        setError(err?.message || 'Erreur lors du chargement des événements');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [paramsKey]);

  // Upsert local (dédoublonne par _id : évite le doublon action directe + écho socket).
  const upsert = useCallback((event) => {
    if (!event?._id) return;
    const id = toStr(event._id);
    setEvents((prev) => {
      const idx = prev.findIndex((e) => toStr(e._id) === id);
      const next =
        idx >= 0 ? prev.map((e, i) => (i === idx ? { ...e, ...event } : e)) : [...prev, event];
      return next.sort(byStart);
    });
  }, []);

  const removeLocal = useCallback((id) => {
    const sid = toStr(id);
    setEvents((prev) => prev.filter((e) => toStr(e._id) !== sid));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refetch();

    if (!socketService.socket) {
      socketService.connect(localStorage.getItem('accessToken'));
    }

    // Réf capturée une fois (cf. patterns socket du projet).
    const socket = socketService.socket;
    if (!socket) return () => { mountedRef.current = false; };

    const handleCreated = (data) => {
      if (mountedRef.current) upsert(data?.event ?? data);
    };
    const handleUpdated = (data) => {
      if (mountedRef.current) upsert(data?.event ?? data);
    };
    const handleDeleted = (data) => {
      if (mountedRef.current) removeLocal(data?.id ?? data?._id ?? data);
    };

    socket.on('calendar:event:created', handleCreated);
    socket.on('calendar:event:updated', handleUpdated);
    socket.on('calendar:event:deleted', handleDeleted);

    return () => {
      mountedRef.current = false;
      socket.off('calendar:event:created', handleCreated);
      socket.off('calendar:event:updated', handleUpdated);
      socket.off('calendar:event:deleted', handleDeleted);
    };
  }, [refetch, upsert, removeLocal]);

  /* ---------- Actions CRUD (mise à jour locale immédiate + écho socket dédoublonné) ---------- */

  const createEvent = useCallback(
    async (payload) => {
      const res = await calendarService.createEvent(payload);
      const event = res?.data ?? res;
      upsert(event);
      return event;
    },
    [upsert]
  );

  const updateEvent = useCallback(
    async (id, payload) => {
      const res = await calendarService.updateEvent(id, payload);
      const event = res?.data ?? res;
      upsert(event);
      return event;
    },
    [upsert]
  );

  const deleteEvent = useCallback(
    async (id) => {
      await calendarService.deleteEvent(id);
      removeLocal(id);
    },
    [removeLocal]
  );

  const updateStatus = useCallback(
    async (id, status) => {
      const res = await calendarService.updateStatus(id, status);
      const event = res?.data ?? res;
      upsert(event);
      return event;
    },
    [upsert]
  );

  return {
    events,
    loading,
    error,
    refetch,
    createEvent,
    updateEvent,
    deleteEvent,
    updateStatus,
  };
};

export default useCalendarEvents;
